// https://ui.shadcn.com/docs/components/dialog
// https://github.com/radix-ui/primitives/issues/1836
// https://codesandbox.io/embed/r9sq1q

import { useContextAndErrorIfNull } from '@listening/utils/react-helpers';
import React, { createContext } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../radix/dropdown-menu';
import { Dialog, DialogTrigger } from '../radix/dialog';

const DialogDropDownContext = createContext<{
  itemSelected: () => void;
  dialogOpenChanged: (open: boolean) => void;
} | null>(null);

export function DialogDropdown<
  T extends {
    focus: HTMLOrSVGElement['focus'];
  },
>({
  trigger,
  className,
  children,
  align,
  collisionPadding,
  onOpenChange,
}: {
  trigger: (props: { ref: React.Ref<T> }) => React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  align?: 'start' | 'center' | 'end';
  collisionPadding?: number;
  onOpenChange?: (open: boolean) => void;
}) {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      setDropdownOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );
  const [hasOpenDialog, setHasOpenDialog] = React.useState(false);

  const dropdownTriggerRef = React.useRef<T>(null);
  const focusRef = React.useRef<T>(null);

  const dialogItemSelected = () => {
    focusRef.current = dropdownTriggerRef.current;
  };

  const dialogItemOpenChanged = (open: boolean) => {
    setHasOpenDialog(open);
    if (!open) setDropdownOpen(false);
  };

  return (
    <DropdownMenu
      modal={false}
      onOpenChange={handleOpenChange}
      open={dropdownOpen}
    >
      <DropdownMenuTrigger asChild>
        {trigger({ ref: dropdownTriggerRef })}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        {...(align === undefined ? {} : { align })}
        className={className}
        collisionPadding={collisionPadding ?? 8}
        hidden={hasOpenDialog}
        onCloseAutoFocus={(e) => {
          if (focusRef.current) {
            focusRef.current.focus();
            focusRef.current = null;
            e.preventDefault();
          }
        }}
      >
        <DialogDropDownContext
          value={{
            itemSelected: dialogItemSelected,
            dialogOpenChanged: dialogItemOpenChanged,
          }}
        >
          {children}
        </DialogDropDownContext>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function DialogItem({
  dialogContent,
  children,
  className,
}: {
  dialogContent: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const { itemSelected, dialogOpenChanged } = useContextAndErrorIfNull(
    DialogDropDownContext,
  );

  const closeDialog = React.useCallback(() => {
    setOpen(false);
    dialogOpenChanged(false);
  }, [dialogOpenChanged]);

  return (
    <DialogCloseContext value={{ closeDialog, open }}>
      <Dialog
        onOpenChange={(val) => {
          setOpen(val);
          dialogOpenChanged(val);
        }}
        open={open}
      >
        <DialogTrigger asChild>
          <DropdownMenuItem
            className={className}
            onSelect={(e) => {
              e.preventDefault();
              itemSelected();
            }}
          >
            {children}
          </DropdownMenuItem>
        </DialogTrigger>
        {dialogContent}
      </Dialog>
    </DialogCloseContext>
  );
}

export const DialogCloseContext = React.createContext<{
  closeDialog: () => void;
  open?: boolean;
}>({ closeDialog: () => null });

/**
 * Provides a context for managing the state of a dialog close event.
 * This is an ugly hack. Radix UI does not provide a way to close a dialog from a child component
 * without making the Dialog a controlled component. This is a workaround to allow a child component
 * to close a dialog without making the Dialog a controlled component.
 *
 * @param children - The content to be rendered inside the DialogCloseProvider.
 * @returns The DialogCloseProvider component.
 */
export function DialogCloseProvider({
  children,
  open: openProp,
  onOpenChange: onOpenChangeProp,
}: {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);

  const open = openProp ?? internalOpen;
  const onOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setInternalOpen(newOpen);
      onOpenChangeProp?.(newOpen);
    },
    [onOpenChangeProp],
  );

  const closeDialog = React.useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <DialogCloseContext value={{ closeDialog, open }}>
      <Dialog onOpenChange={onOpenChange} open={open}>
        {children}
      </Dialog>
    </DialogCloseContext>
  );
}
